import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Drawer, Paper } from '@mui/material';
import styles from '../../../styles/testcase/testcsaedetailsview.module.scss';
import { Tooltip, Typography, Chip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useSelector } from 'react-redux';
import { XMarkIcon, PencilIcon, DocumentTextIcon, TagIcon, ClockIcon, SparklesIcon } from '@heroicons/react/24/outline';
import ResourceNotFound from '../../NotFound/ResourceNotFound';
import NotFoundResourceIcon from '../../NotFound/NotFoundResourceIcon';
import LoadingSpinner from '../../Loaders/LoadingSpinner';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useTheme } from '@mui/material/styles';
import { useLocation } from "react-router-dom";

import '../TestCaseDetailsView.css';
import TestCaseDescription from '../TestCaseDescriptionTab';
import TestcaseAutomationTab from '../TestcaseAutomationTab';
import TabView from '../../tabView/TabView';
import ExecutionHistoryTab from '../ExecutionHistoryTab';
import AutomationStatusChip from '../AutomationStatusChip';
import { fetchTestCaseDetails, updateTestCaseField, checkExecutionPlanExists } from '../../../utils/api';
import { STATUS } from '../../../utils/api/sharedfunction';
import { formatStatusKey } from '../../TestLaunchDetail/statusColorPallete';
import { useSnackbar } from '../../../utils/useSnackbar';
import { breakpoints } from '../../../theme/theme';
import ButtonComponent from '../../customButton';
import NLPAutomation from '../../(Automation)/NLPAutomation';


const TestCaseDetailsView = ({ open, onClose, onEdit }) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const testCaseId = searchParams.get("testCaseId");
    const user = useSelector((state) => state.auth.user);
    const projectId = useSelector((state) => state.projects.selectedProject?.id);
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const [isTestCaseAutomationTabVisible, setIsTestCaseAutomationTabVisible] = useState(false);
    const [testCaseData, setTestCaseData] = useState({});
    const [isLoadingTestCaseData, setIsLoadingTestCaseData] = useState(true);
    const [testCaseNotFound, setTestCaseNotFound] = useState(false);
    const [isUpdatingAutomationStatus, setIsUpdatingAutomationStatus] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const testCaseIdRef = useRef(testCaseId);
    testCaseIdRef.current = testCaseId;

    const fetchTestCaseDetailsById = useCallback(async ({ silent = false } = {}) => {
      const requestTestCaseId = testCaseIdRef.current;
      if (!requestTestCaseId) return;

      if (!silent) {
        setIsLoadingTestCaseData(true);
        setTestCaseNotFound(false);
        setTestCaseData({});
      }

      try {
        const response = await fetchTestCaseDetails({
          testCaseId: requestTestCaseId,
          lastExecutionResultStatus: true,
        });
        if (requestTestCaseId !== testCaseIdRef.current) return;

        if (response.status === STATUS.SUCCESS && response.data) {
          setTestCaseNotFound(false);
          setTestCaseData(response.data);
        } else {
          setTestCaseNotFound(true);
          setTestCaseData({});
        }
      } finally {
        if (requestTestCaseId === testCaseIdRef.current && !silent) {
          setIsLoadingTestCaseData(false);
        }
      }
    }, []);

    useLayoutEffect(() => {
      if (!testCaseId) {
        setIsLoadingTestCaseData(false);
        return;
      }
      setTestCaseNotFound(false);
      setTestCaseData({});
      setIsLoadingTestCaseData(true);
      setActiveTab(0);
    }, [testCaseId]);

    useEffect(() => {
      if (!testCaseId) return;
      fetchTestCaseDetailsById();
    }, [testCaseId, fetchTestCaseDetailsById]);
  
    useEffect(() => {
      const hasAutomationPermission = user?.permissions?.some(permission => {
        return ["execute_test_case_automation_plan", "create_test_case_automation_plan"].includes(permission);
      });
      if (hasAutomationPermission) {
        setIsTestCaseAutomationTabVisible(true);
        return;
      };
      if (user?.project_roles_permissions?.projectRoles[projectId]?.length > 0) {
        user?.project_roles_permissions?.projectRoles[projectId]?.forEach(data => {
          data.permissions.forEach(permission => {
            if (["execute_test_case_automation_plan", "create_test_case_automation_plan"].includes(permission)) {
              setIsTestCaseAutomationTabVisible(true);
              return;
            }
          });
        });
      }
    }, [user.permissions, user.project_roles_permissions]);
  
    // Handle automation status update
    const handleAutomationStatusUpdate = async (updateData) => {
      if (!testCaseId) return;
      
      setIsUpdatingAutomationStatus(true);
      try {
        // Validate NLP Automated status
        if (updateData.automation_status === 'NLP Automated') {
          const hasExecutionPlan = await checkExecutionPlanExists({ testCaseId });
          if (!hasExecutionPlan) {
            throw new Error('Cannot set automation status to "NLP Automated" without a valid execution plan. Please create an execution plan first.');
          }
        }
  
        // Update automation status using lightweight API
        await updateTestCaseField(testCaseId, 'automation_status', updateData.automation_status, user.id);
        
        // Update execution mode if provided
        if (updateData.execution_mode) {
          await updateTestCaseField(testCaseId, 'execution_mode', updateData.execution_mode, user.id);
        }
        
        // Update local state
        setTestCaseData(prev => ({
          ...prev,
          automation_status: updateData.automation_status,
          execution_mode: updateData.execution_mode || prev.execution_mode
        }));
        showSnackbar('Automation status updated successfully!', 'success');
      } catch (error) {
        showSnackbar(error.message || 'Failed to update automation status', 'error');
      } finally {
        setIsUpdatingAutomationStatus(false);
      }
    };
  
    const tabsData = useMemo(() => {
      if (isTestCaseAutomationTabVisible) {
        return [
          {
            name: 'Details',
            icon: <DocumentTextIcon className="icon" style={{ marginRight: 6 }} />,
            component: <TestCaseDescription testCaseData={testCaseData} />
          },
          {
            name: 'NLP Automation',
            icon: <SparklesIcon className="icon" style={{ marginRight: 6 }} />,
            component: <NLPAutomation testCaseId={testCaseData.id} />
          },
          {
            name: 'Execution History',
            icon: <ClockIcon className="icon" style={{ marginRight: 6 }} />,
            component: <ExecutionHistoryTab data={{ id: testCaseId }} refetch={() => fetchTestCaseDetailsById({ silent: true })} />
          }
        ];
      }
      return [
        {
          name: 'Details',
          icon: <DocumentTextIcon className="icon" style={{ marginRight: 6 }} />,
          component: <TestCaseDescription testCaseData={testCaseData} />
        },
        {
          name: 'Execution History',
          icon: <ClockIcon className="icon" style={{ marginRight: 6 }} />,
          component: <ExecutionHistoryTab data={testCaseData} refetch={() => fetchTestCaseDetailsById({ silent: true })} />
        }
      ];
    }, [isTestCaseAutomationTabVisible, testCaseData, testCaseId, fetchTestCaseDetailsById]);
    // Extract module path for breadcrumb
    const modulePath = testCaseData?.module?.modulePath || [];

    // Header icon box style
    const headerIconBoxSx = {  bgcolor: theme.palette.customSidebar.blue600, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' };
    const headerIconColor = { color: '#fff', width: 20, height: 20 };

    const { status } = testCaseData?.lastExecutionResultStatus || {};
    const lastExecutionStatus = formatStatusKey(status) || 'Not Executed';

    const headerTitle = testCaseNotFound
      ? "Not found"
      : isLoadingTestCaseData
        ? "Loading..."
        : testCaseData.key || "";

    const showTestCaseContent = !isLoadingTestCaseData && !testCaseNotFound && Boolean(testCaseData?.id);
  
    return (
        <Drawer
            open={open}
            onClose={onClose}
            anchor="right"
            BackdropProps={{
                onClick: (e) => e.stopPropagation(),
                sx: { backgroundColor: 'rgba(0, 0, 0, 0.2)'},
            }}
            className={styles.silderContainer}
        >
            <Paper className={styles.innerContainer}>
                {/* header */}
                <Box className={styles.headerContainer}>
                    <Box className="icon-cover bg-blue600">
                        <DocumentTextIcon className="icon" />
                    </Box>
                    <Box className={styles.headerTitleContainer}>
                        <Tooltip title={headerTitle}>
                            <Typography variant="componentTitle" sx={{ lineHeight: 1.2 }}>
                                {headerTitle}
                            </Typography>
                        </Tooltip>
                        {showTestCaseContent && (
                            <Box className={styles.subTitleContainer}>
                                {modulePath.length > 0 && modulePath.map((m, idx) => (
                                <React.Fragment key={m.id || m.name || idx}>
                                    <Typography variant="componentTitleSmall">
                                        {m.name}
                                    </Typography>
                                    {idx < modulePath.length - 1 && (
                                        <span className={styles.dot}>•</span>
                                    )}
                                </React.Fragment>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <IconButton
                        className={styles.closeIcon}
                        onClick={onClose}
                        title="Close"
                    >
                        <XMarkIcon className="icon" />
                    </IconButton>
                </Box>
                <Box className={styles.drawerBody}>
                {isLoadingTestCaseData ? (
                    <Box className={styles.drawerBodyState}>
                        <LoadingSpinner
                            message="Loading test case..."
                            size={36}
                            minHeight="100%"
                        />
                    </Box>
                ) : testCaseNotFound ? (
                    <Box className={styles.drawerBodyState}>
                        <ResourceNotFound
                            variant="drawer"
                            icon={<NotFoundResourceIcon type="testCase" />}
                            title="Test case not found"
                            message="This test case may have been deleted, moved, or you may not have access to view it."
                            actionLabel="Close"
                            onAction={onClose}
                        />
                    </Box>
                ) : showTestCaseContent ? (
                <>
                    <Box className={styles.secondaryHeaderContainer}>
                        <Box className={styles.containerOne}>
                            <Box>
                                <p className={styles.title}>Test Case Summary</p>
                                <p className={styles.subtitle}>{testCaseData.title}</p>
                            </Box>
                            <ButtonComponent
                                startIcon={<PencilIcon className="icon"/>}
                                variant="outline"
                                className={styles.editButton}
                                onClick={() => onEdit(testCaseData)}
                                ctaText="Edit"
                            />
                        </Box>
                        <Box className={styles.containerTwo}>
                        <Box>
                            <p className={styles.title}>Type</p>
                            <p className={styles.value}>{testCaseData.type}</p>
                        </Box>

                        <Box>
                            <p className={styles.title}>Is Automated?</p>
                            <AutomationStatusChip
                                value={testCaseData.automation_status}
                                onUpdate={handleAutomationStatusUpdate}
                                disabled={isLoadingTestCaseData || isUpdatingAutomationStatus}
                                testCaseId={testCaseId}
                                isEditing={true}
                            />
                        </Box>

                        <Box>
                            <p className={styles.title}>Last Execution</p>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {lastExecutionStatus === 'Passed' ? (
                                    <CheckCircleIcon className="icon green500" />
                                ) : (
                                    <CheckCircleIcon className="icon slate300" />
                                )}
                                <p className={styles.value}>{lastExecutionStatus}</p>
                            </Box>
                        </Box>
                        <Box>
                            <p className={styles.title}>Labels</p>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {(testCaseData.labels && testCaseData.labels.length > 0) ? (
                                <>
                                {testCaseData.labels.slice(0, 4).map((label) => (
                                    <Chip
                                    key={label.id}
                                    label={
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <TagIcon style={{ width: 10, height: 10, color: '#64748b', marginRight: 4 }} />
                                        {label.name}
                                        </span>
                                    }
                                    sx={{
                                        backgroundColor: "#f1f5f9",
                                        color: "#475569",
                                        fontWeight: 500,
                                        borderRadius: 1,
                                        fontSize: 14,
                                        lineHeight: '20px',
                                        height: 24,
                                        "& .MuiChip-label": {
                                        px: 1.5,
                                        },
                                    }}
                                    />
                                ))}
                                {testCaseData.labels.length > 4 && (
                                    <Chip
                                    label={`+${testCaseData.labels.length - 4} more`}
                                    sx={{
                                        backgroundColor: "#f1f5f9",
                                        color: "#64748b",
                                        fontWeight: 500,
                                        borderRadius: 1,
                                        fontSize: 13,
                                        height: 24,
                                        "& .MuiChip-label": {
                                        px: 1.5,
                                        },
                                    }}
                                    />
                                )}
                                </>
                            ) : (
                                <p className={styles.value}>No label</p>
                            )}
                            </Box>
                        </Box>
                    </Box>
                    </Box>
                    <Box className={styles.contentContainer}>
                        <TabView 
                            tabsData={tabsData} 
                            isTestCaseAutomationTabVisible={isTestCaseAutomationTabVisible}
                            parentActiveTab={activeTab}
                            onTabChange={(event, newValue) => setActiveTab(newValue)}
                        />
                    </Box>
                </>
                ) : (
                    <Box className={styles.drawerBodyState}>
                        <LoadingSpinner
                            message="Loading test case..."
                            size={36}
                            minHeight="100%"
                        />
                    </Box>
                )}
                </Box>
            </Paper>
        </Drawer>
    );
};

export default TestCaseDetailsView;